import React, { CSSProperties, ReactElement, ReactNode, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export interface TailwindTooltipProps {
	reference: ReactElement;
	displayClass: string;
	children: ReactNode;
	position?: 'left' | 'right' | 'top' | 'bottom';
	/** Tailwind width class for the tooltip panel. Defaults to `w-56`. */
	maxWidthClass?: string;
	/** Render the panel under document.body so it is not clipped by overflow containers. */
	portal?: boolean;
	/** Disable tooltip interaction and render only the reference. */
	disabled?: boolean;
	/** Cursor class applied to the reference wrapper. Defaults to `cursor-help`. */
	cursorClassName?: string;
}

export const contentPosition = (type: string) => {
	switch (type) {
		case 'top':
			return 'bottom-full left-1/2 -translate-x-1/2 mb-1.5';
		case 'bottom':
			return 'top-full left-1/2 -translate-x-1/2 mt-1.5';
		case 'left':
			return 'top-1/2 right-full -translate-y-1/2 mr-2.5';
		default:
			return 'top-1/2 left-full -translate-y-1/2 ml-2.5';
	}
};

export const arrowPosition = (type: string) => {
	switch (type) {
		case 'top':
			return 'top-full left-1/2 -translate-x-1/2';
		case 'bottom':
			return 'bottom-full left-1/2 -mb-3 -translate-x-1/2';
		case 'left':
			return 'top-1/2 left-full -ml-1.5';
		default:
			return 'top-1/2 left-0 -ml-1.5';
	}
};

export function TailwindTooltip({
	reference,
	displayClass,
	children,
	position = 'right',
	maxWidthClass = 'w-56',
	portal = false,
	disabled = false,
	cursorClassName = 'cursor-help',
}: TailwindTooltipProps) {
	const [show, setShow] = useState(false);
	const [portalStyle, setPortalStyle] = useState<CSSProperties>();
	const referenceRef = useRef<HTMLDivElement>(null);
	const panelRef = useRef<HTMLDivElement>(null);

	const updatePortalPosition = useCallback(() => {
		if (!portal || !show || referenceRef.current === null) {
			return;
		}

		const referenceRect = referenceRef.current.getBoundingClientRect();
		const panelRect = panelRef.current?.getBoundingClientRect();
		const margin = 8;
		const panelWidth =
			panelRect && panelRect.width > 0 ? panelRect.width : Math.min(320, window.innerWidth - margin * 2);
		const panelHeight = panelRect && panelRect.height > 0 ? panelRect.height : 32;
		let left = referenceRect.left + referenceRect.width / 2 - panelWidth / 2;
		let top = referenceRect.bottom + margin;

		if (position === 'top') {
			top = referenceRect.top - panelHeight - margin;
		} else if (position === 'left') {
			left = referenceRect.left - panelWidth - margin;
			top = referenceRect.top + referenceRect.height / 2 - panelHeight / 2;
		} else if (position === 'right') {
			left = referenceRect.right + margin;
			top = referenceRect.top + referenceRect.height / 2 - panelHeight / 2;
		} else if (top + panelHeight > window.innerHeight - margin) {
			top = referenceRect.top - panelHeight - margin;
		}

		setPortalStyle({
			position: 'fixed',
			zIndex: 1100,
			left: Math.max(margin, Math.min(left, window.innerWidth - panelWidth - margin)),
			top: Math.max(margin, Math.min(top, window.innerHeight - panelHeight - margin)),
		});
	}, [portal, position, show]);

	useLayoutEffect(() => {
		if (!portal || !show) {
			setPortalStyle(undefined);
			return undefined;
		}

		updatePortalPosition();
		window.addEventListener('resize', updatePortalPosition);
		window.addEventListener('scroll', updatePortalPosition, true);

		return () => {
			window.removeEventListener('resize', updatePortalPosition);
			window.removeEventListener('scroll', updatePortalPosition, true);
		};
	}, [portal, show, updatePortalPosition]);

	const handleShowTooltip = () => {
		setShow(true);
	};

	const handleHideTooltip = () => {
		setShow(false);
	};

	if (disabled) {
		return reference;
	}

	const panel = (
		<div
			className={`${portal ? 'fixed pointer-events-none' : 'absolute'} ${maxWidthClass} tailwind-tooltip rounded z-50 ${
				portal ? '' : 'transition-all'
			} ${show ? 'opacity-100 visible' : 'opacity-0 invisible'} ${portal ? '' : contentPosition(position)}`}
			ref={panelRef}
			style={portal ? { position: 'fixed', zIndex: 1100, ...portalStyle } : undefined}
		>
			<div
				className={`relative px-2 py-2 rounded text-[12px] leading-snug font-normal not-italic text-black space-y-2 bg-white whitespace-normal normal-case z-10 ${
					position == 'top' ? 'text-center' : 'text-left'
				}`}
			>
				{children}
			</div>
			<i
				className={`absolute -translate-y-1/2 w-3 h-3 rounded-sm rotate-45 bg-white shadow-md origin-center ${arrowPosition(
					position
				)}`}
			></i>
		</div>
	);

	return (
		<div
			className={`relative ${cursorClassName} ${displayClass}`}
			onMouseEnter={handleShowTooltip}
			onMouseOver={handleShowTooltip}
			onMouseLeave={handleHideTooltip}
			onFocus={handleShowTooltip}
			onBlur={handleHideTooltip}
			ref={referenceRef}
		>
			{reference}
			{portal ? createPortal(panel, document.body) : panel}
		</div>
	);
}
